import { colorConstants } from 'config/colorConstants';
import React, { FC, useContext } from 'react';
import { calculateFontSize } from 'utils/helperMethods';
import { Tooltip } from 'antd';
import { formatDateForTooltip, isValidDateValue } from 'utils/dateHelpers';
import { UserContext } from 'contexts/ConfigContext';

interface Props {
    value: number | string;
    color?: string; // if its undefined no color will be applied
    style?: React.CSSProperties;
    hoverData?: {
        data: (string | number)[];
    };
    kpiName?: string;
}
const scaledWidth = calculateFontSize(window.innerWidth, 100);

const Scorecard: FC<Props> = ({ value, color, style = {}, hoverData, kpiName }) => {
    const { appConfig } = useContext(UserContext);
    const dateFormat = appConfig?.commonConfig?.dateFormat;

    if (!value) return null;

    // Function to render hover content
    const renderHoverContent = () => {
        if (!hoverData || !hoverData.data || hoverData.data.length === 0) {
            return `No additional details available for ${kpiName || 'this metric'}`;
        }

        // Special handling for Weeks KPI with date data
        if (kpiName === 'Weeks' && hoverData.data.length > 0) {
            return (
                <div className="max-w-xs">
                    <div className="font-bold mb-2 text-gray-800">Promo Dates:</div>
                    <div className="grid grid-cols-1 gap-1 max-h-48 overflow-y-auto">
                        {hoverData.data
                            .map((date: string | number, index: number) => {
                                // Validate and format the date using utility function with app config format
                                if (!isValidDateValue(date)) {
                                    console.warn('Invalid date value in hover data:', date);
                                    return null;
                                }

                                return (
                                    <div key={index} className="text-xs text-gray-600">
                                        • {formatDateForTooltip(date, dateFormat)}

                                    </div>
                                );
                            })
                            .filter(Boolean)}
                    </div>
                    <div className="mt-2 text-xs text-gray-500 italic">Total: {hoverData.data.length} weeks</div>
                </div>
            );
        }

        // For other KPIs with data
        return (
            <div className="max-w-xs">
                <div className="font-bold mb-2 text-gray-800">{kpiName} Details:</div>
                <div className="text-xs text-gray-600">
                    {hoverData.data.map((item: any, index: number) => (
                        <div key={index} className="mb-1">
                            • {item}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const scorecardElement = (
        <div
            className="font-xs"
            style={{
                background: color,
                color: colorConstants.blackAlt,
                borderRadius: 4,
                paddingBlock: 7,
                width: scaledWidth,
                fontWeight: 500,
                textAlign: 'center',
                ...style,
            }}
        >
            {value}
        </div>
    );

    // Check if hover data exists and is not empty
    const hasHoverData = hoverData && hoverData.data && hoverData.data.length > 0;

    // If no hover data, return the scorecard without tooltip
    if (!hasHoverData) {
        return scorecardElement;
    }

    // Wrap with tooltip if hover data exists
    return (
        <Tooltip
            title={renderHoverContent()}
            placement="top"
            color="white"
            overlayStyle={{ borderRadius: 8, zIndex: 1000 }}
            overlayInnerStyle={{
                color: colorConstants.darkGrey,
                maxWidth: '300px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
            trigger="hover"
            mouseEnterDelay={0.2}
            mouseLeaveDelay={0.1}
        >
            <div>{scorecardElement}</div>
        </Tooltip>
    );
};

export default Scorecard;
